import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import dayjs from 'dayjs';
import { optionsReceiptTime } from '../../../common/constant';
import * as communeManagementAction from "../../../redux/store/commune-management/commune.store"
import * as herdsmanManagementAction from "../../../redux/store-sarang/herdsman/herdsman-management.store";
import * as cuisineOrderManagementAction from "../../../redux/store-sarang/cuisine-order/cuisine-order-management.store";
import { getUserInfo } from '../../../utils/configuration';

// Sub-components
import StepIndicator from './multistep-form/StepIndicator';
import { formStyles as styles } from './multistep-form/styles';
import Step1BasicInfo from './multistep-form/Step1BasicInfo';
import Step2DeliveryInfo from './multistep-form/Step2DeliveryInfo';
import Step3OrderSummary from './multistep-form/Step3OrderSummary';
import NavigationButtons from './multistep-form/NavigationButtons';

export default function FormAddEditCuisineOrder(props) {
    const { triggerSubmit, setTriggerSubmit, onCloseModal, updateItem,
        district, herdsman, setting,
        onSubmitAddEdit } = props;

    const buttonSubmitRef = useRef(null);
    const [noCookingDate, setNoCookingDate] = useState([6]);

    // Ngày "hôm nay" theo lịch Việt Nam, biểu diễn dưới dạng nửa đêm local để so sánh
    // trực tiếp với giá trị từ DatePicker (vốn cũng là nửa đêm local).
    const getTodayVN = () => dayjs(dayjs().tz().format('YYYY-MM-DD'));

    const isNoCookingDate = (date) => {
        const day = date.day();
        if (noCookingDate.includes(day)) return true;
        const today = getTodayVN();
        const selectedDate = dayjs(date).startOf('day');
        return selectedDate.diff(today, 'day') > 1;
    };

    const getDefaultDate = () => {
        const nowVN = dayjs().tz();
        const today = getTodayVN();
        const isPastOrderTime = nowVN.hour() > setting.openHour || (nowVN.hour() === setting.openHour && nowVN.minute() > setting.openMinute);
        if (isPastOrderTime || isNoCookingDate(today)) {
            let nextDate = today.add(1, 'day');
            for (let i = 0; i < 7 && isNoCookingDate(nextDate); i++) nextDate = nextDate.add(1, 'day');
            return nextDate;
        }
        return today;
    };

    const now = useMemo(() => getDefaultDate(), [setting]);
    // Seed mã đăng ký dùng timestamp thực tại thời điểm mở form để đảm bảo duy nhất
    // (không dùng `now` vì `now` là nửa đêm -> mọi đơn cùng ngày sẽ trùng mã).
    const orderCodeSeed = useMemo(() => `ORDER${dayjs().valueOf()}`, []);

    const methods = useForm({
        mode: "all",
        reValidateMode: "onBlur",
        defaultValues: {
            orderCode: updateItem?.orderCode || orderCodeSeed,
            herdsManOrderId: updateItem?.herdsManOrderId || (getUserInfo()?.herdsmanId || null),
            phoneNumber: updateItem?.phoneNumber || "",
            address: updateItem?.address || "",
            communeId: updateItem?.communeId || null,
            districtId: updateItem?.districtId || null,
            provinceId: updateItem?.provinceId || null,
            quatity: updateItem?.quatity || "",
            receiptTime: updateItem?.receiptTime || 2,
            description: updateItem?.description || "",
            orderDate: updateItem?.orderDate || dayjs(now).format("YYYY-MM-DD"),
            deliveryType: updateItem?.deliveryType ?? 0
        }
    });

    const { register, handleSubmit, setValue, getValues, trigger, watch, formState: { errors } } = methods;
    const formValues = watch();

    const [currentStep, setCurrentStep] = useState(1);
    const [deliveryType, setDeliveryType] = useState(updateItem?.deliveryType ?? 0);
    const [orderCode, setOrderCode] = useState(updateItem?.orderCode || orderCodeSeed);
    const [optionsHerdsman] = useState(herdsman.map(item => ({ label: item.name, value: item.id })));
    const [selectedHerdsman, setSelectedHerdsman] = useState(null);
    const [selectedOrderDate, setSelectedOrderDate] = useState(updateItem?.orderDate ? dayjs(updateItem.orderDate) : dayjs(now));
    const [selectedReceiptTime, setSelectedReceiptTime] = useState(null);
    const [commune, setCommune] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedCommune, setSelectedCommune] = useState(null);
    const [address, setAddress] = useState(updateItem?.address || "");
    const [phoneNumberValue, setPhoneNumberValue] = useState(updateItem?.phoneNumber || "");
    const [quatityValue, setQuatityValue] = useState(updateItem?.quatity || "");
    const [descriptionValue, setDescriptionValue] = useState(updateItem?.description || "");

    // Thông báo hiển thị dưới ô "Ngày nhận" khi đã quá giờ đặt cơm của hôm nay
    // và ngày nhận được tự động chuyển sang ngày tiếp theo (chỉ với đơn tạo mới).
    const dateShiftNotice = useMemo(() => {
        if (updateItem?.id) return null;
        const nowVN = dayjs().tz();
        const isPastOrderTime = nowVN.hour() > setting.openHour || (nowVN.hour() === setting.openHour && nowVN.minute() > setting.openMinute);
        if (!isPastOrderTime) return null;
        const today = getTodayVN();
        const sel = selectedOrderDate ? dayjs(selectedOrderDate).startOf('day') : null;
        if (!sel || !sel.isAfter(today)) return null;
        const hh = setting.openHour;
        const mm = setting.openMinute < 10 ? `0${setting.openMinute}` : setting.openMinute;
        return `Đã quá giờ đặt cơm cho hôm nay (trước ${hh}:${mm} sáng) nên ngày nhận được tự động chuyển sang ngày tiếp theo.`;
    }, [updateItem, setting, selectedOrderDate]);

    const filteredOptionsReceiptTime = useMemo(() => {
        return optionsReceiptTime;
        //if (setting?.branchId === 3) return optionsReceiptTime;
        //return optionsReceiptTime.filter(item => item.value === 2);
    }, [setting?.branchId]);

    // Initial load and setup
    useEffect(() => {
        const initialHerdsmanId = updateItem?.herdsManOrderId || getUserInfo()?.herdsmanId;
        const initialHerdsman = optionsHerdsman.find(item => item.value == initialHerdsmanId);
        setSelectedHerdsman(initialHerdsman || null);

        const initialReceiptVal = updateItem?.receiptTime || 2;
        setSelectedReceiptTime(optionsReceiptTime.find(item => item.value == initialReceiptVal) || null);

        if (updateItem?.communeId) {
            getDetailCommune(updateItem.communeId);
            setSelectedCommune({ label: updateItem?.communeName, value: updateItem?.communeId });
        } else if (!updateItem && getUserInfo()?.herdsmanId) {
            onHerdsmanChange(getUserInfo()?.herdsmanId);
        }

        // Register custom validations
        register("herdsManOrderId", { required: true });
        register("orderDate", {
            required: true,
            validate: (value) => {
                if (!value) return "Trường này là bắt buộc";
                const today = getTodayVN();
                const nowVN = dayjs().tz();
                const dateValue = dayjs(value).startOf('day');
                if (dateValue.isBefore(today)) return "Ngày nhận phải bằng hoặc lớn hơn ngày hiện tại";
                if (!updateItem?.id && dateValue.isSame(today) && (nowVN.hour() > setting.openHour || (nowVN.hour() == setting.openHour && nowVN.minute() > setting.openMinute)))
                    return `Chỉ được đặt suất ăn trước ${setting.openHour}:${setting.openMinute < 10 ? '0' + setting.openMinute : setting.openMinute} sáng cùng ngày`;
                if (noCookingDate.includes(dateValue.day())) return "Nhà hàng không hoạt động vào ngày này";
                if (dateValue.diff(today, 'day') > 1) return "Không được đặt suất ăn quá 1 ngày";
                return true;
            }
        });
        register("quatity", { required: true, validate: v => parseInt(v) > 0 || "Giá trị không hợp lệ" });
        register("phoneNumber", { required: true, maxLength: 20 });
        register("receiptTime", { required: true });
        register("districtId");
        register("communeId", {
            validate: (v) => (getValues("deliveryType") == 0 && !v) ? "Trường này là bắt buộc" : true
        });
        register("address", {
            validate: (v) => (getValues("deliveryType") == 0 && !v) ? "Trường này là bắt buộc" : true
        });
        register("description");
    }, []);

    useEffect(() => {
        if (setting?.branchId != 3 && selectedReceiptTime?.value != 2 && selectedReceiptTime != null) {
            const trua = optionsReceiptTime.find(item => item.value == 2);
            setSelectedReceiptTime(trua);
            setValue("receiptTime", 2);
        }
    }, [setting?.branchId, selectedReceiptTime]);

    const getLookupCommune = async (districtId) => {
        const res = await communeManagementAction.GetLookupCommune(districtId);
        if (res?.content) setCommune(res.content);
    };

    const getDetailCommune = async (communeId) => {
        const res = await communeManagementAction.GetDetailCommune(communeId);
        if (res?.content) {
            setSelectedDistrict({ label: res.content.districtName, value: res.content.districtId });
            setValue("districtId", res.content.districtId);
            getLookupCommune(res.content.districtId);
        }
    };

    const onHerdsmanChange = async (value) => {
        if (!value) {
            setValue("herdsManOrderId", null);
            setSelectedHerdsman(null);
            return;
        }
        setValue("herdsManOrderId", value);
        setSelectedHerdsman(optionsHerdsman.find(item => item.value == value) || null);

        try {
            const res = await herdsmanManagementAction.GetDetailHerdsman(value);
            if (res?.content) {
                setPhoneNumberValue(res.content.phoneNumber);
                setValue("phoneNumber", res.content.phoneNumber);
            }
            const addrRes = await cuisineOrderManagementAction.GetLatestDeliveryAddress(value);
            if (addrRes?.content) {
                const { districtId, districtName, communeId, communeName, address: addr } = addrRes.content;
                setSelectedDistrict({ label: districtName, value: districtId });
                setValue("districtId", districtId);
                setSelectedCommune({ label: communeName, value: communeId });
                setValue("communeId", communeId);
                setAddress(addr || "");
                setValue("address", addr || "");
            }
        } catch (err) { console.error(err); }
    };

    const validateStep = async (step) => {
        const fields = step === 1 ? ["herdsManOrderId", "phoneNumber", "quatity", "orderDate", "receiptTime"] :
            (step === 2 && deliveryType == 0 ? ["districtId", "communeId", "address"] : []);
        return fields.length ? await trigger(fields) : true;
    };

    const handleNext = async () => (await validateStep(currentStep)) && setCurrentStep(s => Math.min(s + 1, 3));
    const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1));
    const onSubmit = (data) => {
        const payload = { ...data, id: updateItem?.id, quatity: parseInt(data.quatity, 10), orderStatus: updateItem?.orderStatus || 10, isActived: updateItem?.isActived ?? true };
        delete payload.districtId;
        onSubmitAddEdit(payload);
        onCloseModal();
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div style={styles.warningContainer}>
                    <div style={styles.warningContent}>
                        <svg style={styles.warningIcon} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <span style={styles.warningTitle}><span>Lưu ý:</span></span>
                            <span style={styles.warningText}><span>ACE giúp đỡ đặt suất ăn trước {setting.openHour}:{setting.openMinute < 10 ? "0" + setting.openMinute : setting.openMinute} sáng cùng ngày. TKAO!</span></span>
                        </div>
                    </div>
                </div>

                <StepIndicator currentStep={currentStep} />

                <div style={styles.stepContentContainer}>
                    {currentStep === 1 && (
                        <Step1BasicInfo key="step1-info"
                            {...{ optionsHerdsman, onHerdsmanChange, selectedHerdsman, selectedOrderDate, setSelectedOrderDate, selectedReceiptTime, setSelectedReceiptTime, filteredOptionsReceiptTime, quatityValue, setQuatityValue, phoneNumberValue, setPhoneNumberValue, isNoCookingDate, orderCode, dateShiftNotice }}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step2DeliveryInfo key="step2-info"
                            {...{ deliveryType, setDeliveryType, selectedDistrict, setSelectedDistrict, selectedCommune, setSelectedCommune, commune, district, address, setAddress, getLookupCommune, branchId: setting?.branchId }}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step3OrderSummary key="step3-info"
                            {...{ descriptionValue, setDescriptionValue, orderCode, getHerdsmanName: () => selectedHerdsman?.label || "Chưa chọn", phoneNumberValue, getReceiptTimeName: () => selectedReceiptTime?.label || "Chưa chọn", deliveryType, selectedCommune, selectedDistrict }}
                        />
                    )}
                </div>

                <NavigationButtons
                    currentStep={currentStep}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    onCloseModal={onCloseModal}
                    onSubmit={() => buttonSubmitRef.current.click()}
                />

                <button ref={buttonSubmitRef} style={{ display: "none" }} type="submit">Lưu</button>
            </form>
        </FormProvider>
    );
}
